import PropTypes from 'prop-types';
import { AppBar, Box, IconButton, Typography, Toolbar } from '@material-ui/core';
import { experimentalStyled, alpha } from '@mui/material/styles';
import MenuIcon from '../../icons/Menu';
import AccountPopover from './AccountPopover';
import ContentSearch from './ContentSearch';
import NotificationsPopover from './NotificationsPopover';
import TaskNotificationsPopover from './TaskNotificationsPopover';
import useSettings from '../../hooks/useSettings';
import Logo from '../Logo';
import LogoBlanc from '../LogoBlanc';
import { toneAlpha, getTone, lightVeil, greyAlpha } from '../../theme/tones';

/**
 * Barre supérieure.
 *
 * Elle court sur TOUTE la largeur et porte la marque : le rail latéral vient
 * se loger dessous, dans la même matière (translucide, floutée, même filet).
 * C'est ce qui fait des deux un seul cadre — un seul CRM — plutôt que deux
 * panneaux posés côte à côte. Liseré « aurora » en signature.
 */
const DashboardNavbarRoot = experimentalStyled(AppBar)(({ theme }) => ({
  backdropFilter: 'blur(16px) saturate(180%)',
  WebkitBackdropFilter: 'blur(16px) saturate(180%)',
  boxShadow: 'none',
  border: 0,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.mode === 'light'
    ? greyAlpha(50, 0.82)
    : greyAlpha(900, 0.72),
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? greyAlpha(900, 0.07) : toneAlpha('neutral', 0.14)}`,
  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  zIndex: theme.zIndex.drawer + 100,
  // Signature « aurora » : un fil de 2px en haut de l'écran.
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 2,
    background: `linear-gradient(90deg, ${getTone('blue').base} 0%, ${getTone('indigo').base} 38%, ${getTone('violet').base} 68%, ${getTone('sky').light} 100%)`,
    opacity: 0.85
  }
}));

const DashboardNavbar = (props) => {
  const { onSidebarMobileOpen, ...other } = props;
  const settings = useSettings();
  const { hostname } = window.location;
  const tenant = hostname.split('.')[0].toUpperCase();
  const isLight = settings.settings.theme === 'LIGHT';

  return (
    <DashboardNavbarRoot
      sx={{ width: '100%', ml: 0 }}
      {...other}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 1.5, lg: 2.5 }, gap: 1.25 }}>
        <IconButton
          color="primary"
          onClick={onSidebarMobileOpen}
          sx={{ display: { lg: 'none' } }}
        >
          <MenuIcon fontSize="small" />
        </IconButton>

        {/* Marque : le logotype vit ici, en tête du cadre, et non plus dans
            le rail. Version blanche en mode sombre, la marine du logotype ne
            se lirait pas sur fond foncé. */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
            pr: 1.5,
            mr: 0.5,
            borderRight: `1px solid ${isLight ? greyAlpha(900, 0.08) : toneAlpha('neutral', 0.16)}`
          }}
        >
          {isLight
            ? <Logo style={{ height: 22, width: 'auto', display: 'block' }} />
            : <LogoBlanc style={{ height: 22, width: 'auto', display: 'block' }} />}
        </Box>

        {/* Contexte : espace client courant. Une pastille avec un point
            « en ligne » plutôt qu'un simple libellé perdu dans la barre. */}
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.9,
            pl: 1.1,
            pr: 1.4,
            py: 0.55,
            borderRadius: '999px',
            backgroundColor: isLight ? greyAlpha(900, 0.04) : lightVeil(0.06),
            border: `1px solid ${isLight ? greyAlpha(900, 0.06) : toneAlpha('neutral', 0.14)}`
          }}
        >
          <Box
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: 'success.main',
              boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.success.main, 0.18)}`
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 800,
              fontSize: '0.68rem',
              letterSpacing: '0.1em',
              lineHeight: 1.6,
              color: 'text.primary'
            }}
          >
            {tenant}
          </Typography>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Actions : regroupées dans un rail arrondi pour former un bloc
            visuel unique au lieu d'icônes éparpillées. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.4,
            py: 0.4,
            borderRadius: '999px',
            backgroundColor: isLight ? greyAlpha(900, 0.035) : lightVeil(0.05),
            border: `1px solid ${isLight ? greyAlpha(900, 0.05) : toneAlpha('neutral', 0.12)}`
          }}
        >
          <ContentSearch />
          <NotificationsPopover />
          <TaskNotificationsPopover />
        </Box>

        <Box sx={{ ml: 1 }}>
          <AccountPopover />
        </Box>
      </Toolbar>
    </DashboardNavbarRoot>
  );
};

DashboardNavbar.propTypes = {
  onSidebarMobileOpen: PropTypes.func
};

export default DashboardNavbar;
